import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Grid, Box } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { CarrierSearchContainer } from 'apps/fleet-planner/components/carrier-search'
import { getRoute, IOnboardingQueryParams } from 'apps/fleet-planner/constants'
import { useOnboardingWizardContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useJoinMotorCarriers } from 'apps/fleet-planner/hooks/useJoinMotorCarriers'
import { Button, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { ISearchedMotorCarrier } from '../carrier-search/types'
import { OnboardingStepContainer } from './OnboardingStepContainer'
/**
 * Container component that handles the API logic around
 * adding a carrier to a user in <OnboardingWizard />
 */
export const OnboardingStepCarriers = () => {
  const { trackEvent } = useFMSAnalytics()
  const { isMobile } = useBreakpointContext()
  const {
    carriers = [],
    addCarrier,
    removeCarrierByIndex,
    mobileApp,
  } = useOnboardingWizardContext()
  const history = useHistory()

  useEffect(() => {
    trackEvent('Onboarding', 'ONBOARDING_VIEWED_ADD_CARRIERS_AT_SIGNUP_PAGE')
  }, [trackEvent])

  const routingHandler = () => {
    // carriers that require admin approval to join
    const unapprovedCarriers = carriers.filter(c => Boolean(c.maskedAdminEmails.length))
    const hasOnlyUnapprovedCarriers = carriers.length === unapprovedCarriers.length
    const hasNoCarriers = !carriers.length

    if (hasOnlyUnapprovedCarriers) {
      trackEvent(
        'Onboarding',
        'ONBOARDING_USER_REQUESTED_TO_JOIN_CARRIER_FROM_ADD_CARRIERS_AT_SIGNUP_PAGE'
      )
      const params: IOnboardingQueryParams = {}
      if (mobileApp) {
        params.mobileApp = 1
      }
      history.push(getRoute('unapprovedCarriers', params))
    } else if (hasNoCarriers) {
      history.push(getRoute('noCarriers'))
    } else if (mobileApp) {
      ReactNativeWebView.postMessage('NEW_CARRIER_ADDED')
    } else {
      history.push(getRoute('loading'))
    }
  }

  const { clearErrorMessages, errorMessages, joinMotorCarriers, loading } = useJoinMotorCarriers({
    onSuccess: () => {
      trackEvent(
        'Onboarding',
        'ONBOARDING_SUCCESSFULLY_ADDED_CARRIER_ON_ADD_CARRIERS_AT_SIGNUP_PAGE'
      )
      routingHandler()
    },
    onError: () => {
      trackEvent('Onboarding', 'ONBOARDING_FAILED_TO_ADD_CARRIER_ON_ADD_CARRIERS_AT_SIGNUP_PAGE')
    },
  })

  const [errorMessage] = errorMessages

  /**
   * Triggers POST endpoint
   */
  const addCarriers = async (): Promise<void> => {
    if (loading) return

    clearErrorMessages()
    await joinMotorCarriers(carriers)
  }

  const addCarrierHandler = (carrier: ISearchedMotorCarrier) => {
    clearErrorMessages()
    addCarrier(carrier)
  }

  const removeCarrierHandler = (index: number) => {
    clearErrorMessages()
    removeCarrierByIndex(index)
  }

  return (
    <OnboardingStepContainer
      title='Which carrier do you work for?'
      fadeIn
      subtitle={
        <>
          <Box display={isMobile ? 'inline' : 'block'}>
            You can add multiple carriers if you dispatch for more than one company.
          </Box>{' '}
          <Box display={isMobile ? 'inline' : 'block'}>You can always add more carriers later.</Box>
        </>
      }>
      <Box>
        <CarrierSearchContainer
          isMobileApp={mobileApp}
          carriers={carriers}
          onAddCarrier={addCarrierHandler}
          onRemoveCarrier={removeCarrierHandler}
        />
      </Box>
      {errorMessage && (
        <Box mt={3}>
          <InlineMessage
            fullWidth
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            message={errorMessage || 'There was an error with updating your information.'}
          />
        </Box>
      )}
      {!!carriers.length && (
        <Box mt={5}>
          <Grid container spacing={3} justifyContent='center'>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                disabled={!carriers.length || loading}
                onClick={addCarriers}
                color='primary'
                data-test='continue-button'
                size='large'>
                {loading ? 'Loading... ' : 'Finish Setup'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </OnboardingStepContainer>
  )
}
